"use client";

import { useRef, useState, useEffect, ReactNode, useCallback } from "react";
import { Box, Title, ActionIcon } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import Link from "@/components/Link";
import { trackEvent } from "@/lib/client/analytics";

const MEDIUM_MEDIA_QUERY = "(min-width: 850px)";

export type BaseCarouselClasses = {
  baseCarousel?: string;
  edgeToEdge?: string;
  header?: string;
  seeMoreLink?: string;
  navButtonLeft?: string;
  navButtonRight?: string;
  wrapper?: string;
};

export type BaseCarouselProps = {
  title?: string;
  titleOrder?: 2 | 3;
  seeMoreLink?: string;
  seeMoreText?: string;
  seeMoreAriaLabel?: string;
  children: ReactNode;
  showNavButtons?: boolean;
  scrollAmount?: number | "page";
  className?: string;
  headerClassName?: string;
  wrapperClassName?: string;
  leftButtonClassName?: string;
  rightButtonClassName?: string;
  onScrollChange?: (canScrollLeft: boolean, canScrollRight: boolean) => void;
  edgeToEdge?: boolean;
  insetNavButtons?: boolean;
  analyticsName?: string;
};

type BaseCarouselCoreProps = BaseCarouselProps & {
  classes: BaseCarouselClasses;
};

const joinClasses = (...classNames: Array<string | undefined | false>) =>
  classNames.filter(Boolean).join(" ");

export const BaseCarouselCore = ({
  title,
  titleOrder = 3,
  seeMoreLink,
  seeMoreText = "See more",
  seeMoreAriaLabel,
  children,
  showNavButtons = true,
  scrollAmount = 400,
  className,
  headerClassName,
  wrapperClassName,
  leftButtonClassName,
  rightButtonClassName,
  onScrollChange,
  edgeToEdge = false,
  insetNavButtons = false,
  analyticsName,
  classes,
}: BaseCarouselCoreProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const newCanScrollLeft = scrollLeft > 0;
    const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 10;

    setCanScrollLeft(newCanScrollLeft);
    setCanScrollRight(newCanScrollRight);

    if (onScrollChange) {
      onScrollChange(newCanScrollLeft, newCanScrollRight);
    }
  }, [onScrollChange]);

  const debouncedCheckScrollPosition = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      checkScrollPosition();
    }, 150);
  }, [checkScrollPosition]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const mediaQuery = window.matchMedia(MEDIUM_MEDIA_QUERY);
    let desktopListenersAttached = false;
    let resizeObserver: ResizeObserver | null = null;
    let initialCheckTimer: ReturnType<typeof setTimeout> | null = null;

    const detachDesktopListeners = () => {
      if (!desktopListenersAttached) return;

      container.removeEventListener("scroll", debouncedCheckScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (initialCheckTimer) {
        clearTimeout(initialCheckTimer);
        initialCheckTimer = null;
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      desktopListenersAttached = false;
    };

    const attachDesktopListeners = () => {
      if (desktopListenersAttached || !mediaQuery.matches) return;

      desktopListenersAttached = true;
      container.addEventListener("scroll", debouncedCheckScrollPosition);
      window.addEventListener("resize", checkScrollPosition);

      resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });
      resizeObserver.observe(container);

      initialCheckTimer = setTimeout(() => {
        initialCheckTimer = null;
        checkScrollPosition();
      }, 100);
    };

    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        attachDesktopListeners();
      } else {
        detachDesktopListeners();
      }
    };

    attachDesktopListeners();
    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
      detachDesktopListeners();
    };
  }, [checkScrollPosition, debouncedCheckScrollPosition]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (analyticsName) {
      try {
        trackEvent("carousel_engagement", "engagement", {
          carousel_type: analyticsName,
          action: `nav_${direction}`,
          device_type:
            window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
        });
      } catch {
        // Analytics must never affect carousel navigation
      }
    }

    const distance = scrollAmount === "page" ? container.clientWidth : scrollAmount;
    const newScrollLeft = container.scrollLeft + (direction === "right" ? distance : -distance);

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  return (
    <Box
      component="section"
      className={joinClasses(classes.baseCarousel, edgeToEdge && classes.edgeToEdge, className)}
      data-nav-buttons-inset={insetNavButtons || undefined}
      pos="relative"
    >
      {(title || seeMoreLink) && (
        <Box className={headerClassName || classes.header}>
          {title && <Title order={titleOrder}>{title}</Title>}
          {seeMoreLink && (
            <Link href={seeMoreLink} className={classes.seeMoreLink} aria-label={seeMoreAriaLabel}>
              {seeMoreText}
            </Link>
          )}
        </Box>
      )}

      {showNavButtons && (
        <>
          <ActionIcon
            onClick={() => scroll("left")}
            className={leftButtonClassName || classes.navButtonLeft}
            aria-label="Scroll left"
            disabled={!canScrollLeft}
            variant="unstyled"
          >
            <IconChevronLeft size={20} stroke={2} />
          </ActionIcon>

          <ActionIcon
            onClick={() => scroll("right")}
            className={rightButtonClassName || classes.navButtonRight}
            aria-label="Scroll right"
            disabled={!canScrollRight}
            variant="unstyled"
          >
            <IconChevronRight size={20} stroke={2} />
          </ActionIcon>
        </>
      )}

      <Box className={joinClasses(classes.wrapper, wrapperClassName)} ref={scrollContainerRef}>
        {children}
      </Box>
    </Box>
  );
};
